import getClvCurrencyAndValue from "@helpers/getClvCurrencyAndValue"
import { hashValue } from "@helpers/hashValue"
import { getFbcFromLocalStorage } from "@helpers/localStorage"
import {
  getUserCity,
  getUserCountryCode,
  getUserEmail,
  getUserId,
  getUserZipCode,
} from "@helpers/sessionStorage"
import { trackEvent } from "@helpers/supabase"
import * as Sentry from "@sentry/react"
import Cookies from "js-cookie"
import { EventName } from "src/types"
import { v4 as uuidv4 } from "uuid"

export async function trackCompleteRegistrationEvent() {
  const eventId = createEventId()
  const eventName = "CompleteRegistration"

  await trackEventFromServer({ eventId, eventName })
  await trackEventFromClient({ eventId, eventName })
}

export async function trackSubscriptionEvent({
  currency,
  isPurchase,
  planPrice,
  stripeFirstPriceId,
  stripeNextPriceId,
}: {
  currency: string
  isPurchase: boolean
  planPrice: number
  stripeFirstPriceId: string
  stripeNextPriceId: string
}) {
  const { clvCurrency, clvValue } = await getClvCurrencyAndValue({
    currency,
    stripeFirstPriceId,
    stripeNextPriceId,
  })

  const eventId = createEventId()
  const eventName = isPurchase ? "Purchase" : "InitiateCheckout"

  await Promise.all([
    trackEventFromClient({
      currency: clvCurrency,
      eventId,
      eventName,
      value: clvValue,
    }),
    trackEventFromServer({
      currency: clvCurrency,
      eventId,
      eventName,
      value: clvValue,
      planPrice,
    }),
  ])
}

function createEventId(): string {
  return uuidv4()
}

async function trackEventFromClient({
  currency,
  eventId,
  eventName,
  value,
}: {
  currency?: string
  eventId: string
  eventName: EventName
  value?: number
}) {
  const userCity = await getUserCity()
  const userCountryCode = (await getUserCountryCode()).toLowerCase()
  const userEmail = await getUserEmail()
  const userId = await getUserId()
  const userZipCode = await getUserZipCode()

  const payload =
    eventName === "CompleteRegistration" ? {} : { currency, value }

  return new Promise<void>((resolve, reject) => {
    if (fbq) {
      fbq("init", import.meta.env.VITE_FACEBOOK_PIXEL_ID, {
        country: userCountryCode,
        ct: userCity,
        em: userEmail,
        external_id: userId,
        zp: userZipCode,
      })

      fbq("track", eventName, payload, {
        eventID: eventId,
      })

      resolve()
    } else {
      Sentry.captureException("Facebook Pixel not loaded")
      reject(new Error("Facebook Pixel not loaded"))
    }
  })
}

async function trackEventFromServer({
  currency,
  eventId,
  eventName,
  value,
  planPrice,
}: {
  currency?: string
  eventId: string
  eventName: EventName
  value?: number
  planPrice?: number
}) {
  const eventSourceUrl = window.location.href
  const fbcFromCookie = Cookies.get("_fbc")
  const fbcFromLocalStorage = getFbcFromLocalStorage()
  const fbc = fbcFromCookie ?? fbcFromLocalStorage
  const fbp = Cookies.get("_fbp")
  const userCity = await getUserCity()
  const userCountryCode = (await getUserCountryCode()).toLowerCase()
  const userEmail = await getUserEmail()
  const userId = await getUserId()
  const userZipCode = await getUserZipCode()

  await trackEvent({
    currency,
    eventId,
    eventName,
    eventSourceUrl,
    fbc,
    fbcFromCookie,
    fbcFromLocalStorage,
    fbp,
    pixelId: import.meta.env.VITE_FACEBOOK_PIXEL_ID as string,
    userCity: await hashValue(userCity),
    userCountryCode: await hashValue(userCountryCode),
    userEmail: await hashValue(userEmail),
    userId: userId,
    userZipCode: await hashValue(userZipCode),
    value,
    planPrice,
  })
}
